/**
 * Subscriber entity class
 */

import moment from 'moment'
import { PLATFORM_PREFIXES } from '../../../app/core/constants'
import { dateAsUTC } from '../../../app/helpers/date'
import { has } from '../../../app/helpers/object'

/**
 * SubscriberFactory
 * returns Subscriber entity class
 *
 * @class SubscriberFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Subscriber} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SubscriberFactory
 */
/* @ngInject */
export default function SubscriberFactory ($http, authSvc, alertLiteSvc, Type, Entity, Note, AccessoryNote, onOffMixin) {
  /* === Subscriber class === */

  /**
   * Subscriber model
   *
   * @class Subscriber
   * @classdesc Subscriber model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.Subscriber} Subscriber entity
   */
  function Subscriber () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Subscriber.prototype = Object.create(Entity.prototype)
  Subscriber.prototype.constructor = Subscriber

  /* === Subscriber instance methods & fields === */

  Object.assign(Subscriber.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Subscriber#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      $cachedPhone: Number,
      $dateOfBirth: Date,
      $platformPrefix: String,
      $ssoId: String,
      $tvsAccountStartDate: Date,
      accessoryNotes: ['AccessoryNote'],
      accountNumber: String,
      address: String,
      city: String,
      country: String,
      creditCards: ['CreditCard'],
      currentPaymentStatement: 'PaymentStatement',
      customChannels: ['ChannelCustom'],
      customVods: ['VODCustom'],
      dateOfBirth: String,
      deleted: Boolean,
      deviceSlots: ['DeviceSlot'],
      devices: ['Device'],
      email: String,
      enabled: Boolean,
      expirationTime: moment,
      firstname: String,
      foreignPlatformSubscriberId: String,
      hasUnlimitedSubscription: Boolean,
      language: String,
      lastAccess: moment,
      lastname: String,
      network: 'Network',
      notes: ['Note'],
      operator: String,
      password: String,
      paymentStatements: ['PaymentStatement'],
      phone: String,
      backupPhoneNumber: String,
      pincode: String,
      registered: moment,
      remainDaysRetryPeriod: Number,
      state: String,
      timeZone: String,
      tvsAccountNumber: String,
      tvsAccountStartDate: String,
      tvsThaiId: String,
      type: String,
      user: 'User',
      zipcode: String
    },

    /**
     * url template
     *
     * @memberof models.Subscriber#
     * @type {String}
     */
    _urlTemplate: 'api/subscribers/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Subscriber#
     * @type {String}
     */
    _publicUrlTemplate: 'subscribers/{{::id}}',

    /**
     * url template
     *
     * @memberof models.Subscriber#
     * @type {String}
     */
    _urlAddonsTemplate: 'api/subscribers/{{::id}}/extra?contentAddons',

    /**
     * Prepare channel
     *
     * @memberof models.Channel#
     * @override
     */
    _prepare: function () {
      this.$dateOfBirth = this.dateOfBirth === null ? null : new Date(this.dateOfBirth)
      this.$tvsAccountStartDate = this.tvsAccountStartDate === null ? null : new Date(this.tvsAccountStartDate)
      this.$cachedPhone = this.phone
      Object.keys(PLATFORM_PREFIXES).forEach((key) => {
        if (this.foreignPlatformSubscriberId?.includes(PLATFORM_PREFIXES[key])) {
          this.$platformPrefix = PLATFORM_PREFIXES[key]
          this.$ssoId = this.foreignPlatformSubscriberId.substring(PLATFORM_PREFIXES[key].length)
        }
      })
      if (authSvc.hasEnabledFlag('TRUE_VISIONS_FEATURES_ENABLED') && !this.$platformPrefix) {
        this.$platformPrefix = PLATFORM_PREFIXES.tvs
      }
    },

    /**
     * Prepare channel
     *
     * @memberof models.Channel#
     * @override
     */
    _object: function () {
      this.tvsAccountStartDate = this.$tvsAccountStartDate ? dateAsUTC(this.$tvsAccountStartDate, 'YYYY-MM-DD') : null
      this.dateOfBirth = this.$dateOfBirth ? dateAsUTC(this.$dateOfBirth, 'YYYY-MM-DD[T]HH:mm') : null
      if (this.$ssoId) {
        this.foreignPlatformSubscriberId = this.$platformPrefix + this.$ssoId
      } else {
        this.foreignPlatformSubscriberId = null
      }
      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get user status
     *
     *   Enabled and Non expired -> Active
     *   Disabled and Non expired -> Suspended
     *   Enabled and Expired -> Expired
     *   Disabled and Expired -> Expired
     *
     * @memberof models.Subscriber#
     * @param {String} w - 't' => text, 'c' => className
     * @return {Number|String} 0 - expired 1 - suspended, 2 - active, 3 - on hold
     */
    _status: function (w = 't') {
      const isExpired = !this.currentPaymentStatement
      const isSuspended = !isExpired && !this.enabled
      const isActive = !isSuspended && (!this.currentPaymentStatement || this.currentPaymentStatement.status === 'ACTIVE')
      const isOnHold = !isActive && this.currentPaymentStatement && this.currentPaymentStatement.status === 'ON_HOLD'
      const isPending = !isActive && this.currentPaymentStatement && this.currentPaymentStatement.status === 'PENDING'
      return {
        t: ['Expired', 'Suspended', 'Active', 'TOA Pending', 'Pending', 'Payment Pending', 'Declined', '???'], // should never be '???'
        c: ['red', 'orange', 'green', 'green', 'lightgreen', 'lightgreen', 'red', 'blue'] // should never be 'blue'
      }[w][isExpired ? 0 : isSuspended ? 1 : isActive ? 2 : isOnHold ? 3 : isPending ? 4 : 5] // should never be 5
    },

    /**
     * Get count of days, left before expiration
     *
     * @memberof models.Subscriber#
     * @param {Boolean} toYMWD - return value as string with Years, Months, Weeks and Days
     * @param {Object} fakeNow - use for only test!!!
     * @return {Number}
     */
    _daysToExpire: function (toYMWD, fakeNow) {
      if (!this.expirationTime) {
        return toYMWD ? '-' : 0
      }

      var now = moment(fakeNow)
      var exp = moment(this.expirationTime)
      var ret

      if (!toYMWD) {
        ret = exp.diff(now, 'days')
        return ret > 0 ? ret + 1 : 0
      }

      var years = exp.diff(now, 'years')
      exp.subtract(years, 'years')
      years = Math.abs(years)

      var months = exp.diff(now, 'months')
      exp.subtract(months, 'months')
      months = Math.abs(months)

      var weeks = exp.diff(now, 'weeks')
      exp.subtract(weeks, 'weeks')
      weeks = Math.abs(weeks)

      var days = exp.diff(now, 'days')
      days = Math.abs(days)
      if (days === 0 && (!years && !months && !weeks)) {
        return '0D'
      }

      return (exp.isAfter(now) ? '' : '-') +
             (years ? years + 'Y' : '') + (years && (months || weeks || days) ? ',' : '') +
             (months ? months + 'M' : '') + (months && (weeks || days) ? ',' : '') +
             (weeks ? weeks + 'W' : '') + (weeks && days ? ',' : '') +
             (days || (!years && !months && !weeks) ? days + 'D' : '')
    },

    /**
     * Get current main subscription
     *
     * @memberof models.Subscriber#
     * @return {models.Subscription}
     */
    _currentMainSubscription: function () {
      if (this.currentPaymentStatement) {
        return this.currentPaymentStatement._mainSubscription()
      }
    },

    /**
     * Get paid devices count
     *
     * @memberof models.Subscriber#
     * @return {models.Subscription}
     */
    _paidDeviceCount: function () {
      if (this.__paidDeviceCount === undefined) {
        var shouldCountPaidDevices = !this.currentPaymentStatement
        var paidDevices = shouldCountPaidDevices ? 0 : this.currentPaymentStatement.paidDevices
        var enabledDevices = 0

        if (this.devices && this.devices.length) {
          for (var i = this.devices.length; i--;) {
            enabledDevices += (this.devices[i].enabled ? 1 : 0)
            paidDevices += shouldCountPaidDevices ? (this.devices[i].status ? 1 : 0) : 0
          }
        }

        this.__paidDeviceCount = Math.min(paidDevices, enabledDevices)
      }
      return this.__paidDeviceCount
    },

    /**
     * On/Off Autopay for subscriber, performing PATCH request to the server
     *
     * @memberof models.Subscriber#
     * @param {Boolean} [autoPay=this.currentPaymentStatement.autoPay] - enable or disable autoPay
     * @return {Promise} promise
     */
    _onOffAutopay: function (autoPay) {
      if (typeof autoPay !== 'boolean') {
        autoPay = this.currentPaymentStatement && this.currentPaymentStatement.autoPay
      }

      return $http.patch(this._url, autoPay ? '"ENABLE_AUTOPAY"' : '"DISABLE_AUTOPAY"')
        .catch(
          rejection => {
            if (this.currentPaymentStatement) {
              this.currentPaymentStatement.autoPay = !this.currentPaymentStatement.autoPay
            }
            return alertLiteSvc.alertError(rejection)
          }
        )
    },

    /**
     * Obtain subscriber's password
     *
     * @memberof models.Subscriber#
     * @return {Promise} promise
     */
    _getPassword: function () {
      return $http.get(this._url + '/pass')
        .catch(alertLiteSvc.alertError)
    },

    _getRedirectLinkStatus: function () {
      return $http.get(this._url + '/portal-url/status')
        .then(response => response?.data?.payload)
        .catch(() => undefined)
    },

    _getRedirectLink: function (params) {
      return $http.post(this._url + '/portal-url', params)
        .then(response => response?.data?.payload?.redirectUrl)
        .catch(() => undefined)
    },

    /**
     * Search devices by param
     *
     * @param {String} search
     * @returns {Promise} promise
     */
    _searchDevices: function (search) {
      return $http.post(this._url + '/extra?devices', { searchStr: search })
        .catch(rejection => alertLiteSvc.alertError(rejection))
    }

  }, onOffMixin)

  /* === Subscriber static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Subscriber, Entity)
  Subscriber._name = 'Subscriber'
  Subscriber.init()

  // permissions
  Subscriber.access = {
    view: 'viewSubscriber',
    create: 'createSubscriber',
    edit: 'editSubscriber editSearch |',
    delete: 'deleteSubscriber deleteSearch |'
  }

  // extra
  Subscriber.extra = {
    active: false,
    activeRentContentAddOns: [
      {
        amount: Number,
        availableTo: Date,
        currency: 'Currency',
        length: Number,
        extraLengthDays: Number,
        name: String,
        purchaseItemCategory: String,
        purchaseItemId: Number,
        unit: String
      }
    ],
    addDevicesInfoOverride: {
      addDeviceCurrency: 'Currency',
      allowedDevicesToPay: 0,
      alreadyPaidDevices: 0
    },
    addDevicesInfoTopUp: {
      addDeviceCurrency: 'Currency',
      allowedDevicesToPay: 0,
      alreadyPaidDevices: 0
    },
    addOns: ['Subscription'],
    currencyConverterTypes: [],
    currentPSIsLast: Boolean,
    hasUpcomingSubscription: Boolean,
    currentPaymentStatementAutoPayAllowed: Boolean,
    currentSubscription: {
      custom: Boolean,
      hasContentAddons: Boolean,
      id: Number,
      name: String,
      trial: Boolean
    },
    customStreamCount: 0,
    deviceCount: 0,
    devices: ['Device'],
    misc: ['Subscription'],
    networks: ['Network'],
    networkSettings: null, // map <name,value>, `null` will do nothing to field value
    noteCount: 0,
    accessoryNoteCount: 0,
    paymentSystems: ['NetworkPaymentSystem'],
    subscriptions: ['Subscription'],
    transactionFeeEnabled: Boolean,
    trial: {
      allowed: false,
      activeFrom: null,
      activeUntil: null
    },
    passwordPolicies: [
      { name: String, networkIds: [Number], pattern: String }
    ]
  }

  /**
   *
   */
  Subscriber._getSubscribers = function (params) {
    const url = 'api/subscribers'

    if (!params.q) {
      delete params.q
    }
    if (params.last !== undefined) {
      delete params.last
    }
    return $http.get(url, { params })
      .data()
      .then((response) => response.content)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get subscriber notes
   */

  Subscriber._getNotes = function (tableState, subscriberId) {
    var params = {
      'sort-by': tableState.sort.predicate || '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)

    if (params.createTime) {
      params['created-from'] = JSON.parse(params.createTime).after
      params['created-to'] = JSON.parse(params.createTime).before
      delete params.createTime
    }
    if (params.createdBy) {
      params['created-by'] = params.createdBy
      delete params.createdBy
    }

    const url = `/nora/api/v2/subscribers/${subscriberId}/notes`
    return $http.get(url, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        var result = this.cast(data, {
          // response structure
          content: [],
          first: true,
          last: true,
          number: 0,
          totalPages: 0,
          totalElements: 0,
          size: 0,
          sort: [{
            ascending: true,
            direction: '',
            ignoreCase: true,
            nullHandling: '',
            property: ''
          }]
        })

        result.content.forEach(note => { note.description = Note.insertImgIntoDiv(note.description) })

        if (Array.isArray(result.sort) && result.sort.length) {
          var last = result.sort.pop()
          tableState.sort.predicate = last.property
          tableState.sort.reverse = !last.ascending
        }
        tableState.pagination.start = result.number * result.size // first element
        tableState.pagination.number = result.size // page size
        tableState.pagination.numberOfPages = result.totalPages
        tableState.pagination.totalItemCount = result.totalElements
        return result
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  Subscriber._getAccessoryNotes = function (tableState, subscriberId) {
    var params = {
      'sort-by': tableState.sort.predicate || '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)

    if (params.createTime) {
      params['created-from'] = JSON.parse(params.createTime).after
      params['created-to'] = JSON.parse(params.createTime).before
      delete params.createTime
    }

    const url = `/nora/api/subscribers/${subscriberId}/accessory-notes`
    return $http.get(url, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        var result = this.cast(data, {
          // response structure
          content: [AccessoryNote],
          first: true,
          last: true,
          number: 0,
          totalPages: 0,
          totalElements: 0,
          size: 0,
          sort: [{
            ascending: true,
            direction: '',
            ignoreCase: true,
            nullHandling: '',
            property: ''
          }]
        })

        if (Array.isArray(result.sort) && result.sort.length) {
          var last = result.sort.pop()
          tableState.sort.predicate = last.property
          tableState.sort.reverse = !last.ascending
        }
        tableState.pagination.start = result.number * result.size // first element
        tableState.pagination.number = result.size // page size
        tableState.pagination.numberOfPages = result.totalPages
        tableState.pagination.totalItemCount = result.totalElements
        return result
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get purchases
   *
   * @memberof models.Subscriber#
   */
  Subscriber.getInappPurchases = function (tableState, subscriberId) {
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number || 25
    }
    Object.assign(params, tableState.search.predicateObject)

    if (params.processDate) {
      params['process-date-from'] = JSON.parse(params.processDate).after
      params['process-date-to'] = JSON.parse(params.processDate).before
      delete params.processDate
    }

    if (params.subscriptionStatuses) {
      params['subscription-statuses'] = params.subscriptionStatuses
      delete params.subscriptionStatuses
    }

    if (params.purchaseNumber) {
      params['purchase-number'] = params.purchaseNumber
      delete params.purchaseNumber
    }

    if (params.processedBy) {
      params['processed-by'] = params.processedBy
      delete params.processedBy
    }

    if (params.paymentStatus) {
      params['payment-status'] = params.paymentStatus
      delete params.paymentStatus
    }

    if (params.transactionId) {
      params['transaction-id'] = params.transactionId
      delete params.transactionId
    }

    if (params.captureId) {
      params['capture-id'] = params.captureId
      delete params.captureId
    }

    const url = `/nora/api/subscribers/${subscriberId}/purchases`

    return $http.get(url, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        var result = this.cast(data, {
          // response structure
          content: ['PaymentStatement'],
          first: true,
          last: true,
          number: 0,
          totalPages: 0,
          totalElements: 0,
          size: 0,
          sort: [{
            ascending: true,
            direction: '',
            ignoreCase: true,
            nullHandling: '',
            property: ''
          }]
        })

        if (Array.isArray(result.sort) && result.sort.length) {
          var last = result.sort.pop()
          tableState.sort.predicate = last.property
          tableState.sort.reverse = !last.ascending
        }
        tableState.pagination.start = result.number * result.size // first element
        tableState.pagination.number = result.size // page size
        tableState.pagination.numberOfPages = result.totalPages
        tableState.pagination.totalItemCount = result.totalElements
        return result
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get Partner Products
   *
   * @memberof models.Subscriber#
   */
  Subscriber.getPartnerProducts = function (tableState, subscriberId) {
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number || 25,
      status: tableState.search.predicateObject?.status || '',
      name: tableState.search.predicateObject?.name || ''
    }

    const url = `/nora/api/subscribers/${subscriberId}/partner-product-access`

    return $http.get(url, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        var result = this.cast(data, {
          // response structure
          content: [{
            id: Number,
            productName: String,
            availableFrom: String,
            availableTill: String,
            status: String,
            partnerProductLink: String,
            errorNote: String
          }],
          first: true,
          last: true,
          number: 0,
          totalPages: 0,
          totalElements: 0,
          size: 0,
          sort: [{
            ascending: true,
            direction: '',
            ignoreCase: true,
            nullHandling: '',
            property: ''
          }]
        })

        if (Array.isArray(result.sort) && result.sort.length) {
          var last = result.sort.pop()
          tableState.sort.predicate = last.property
          tableState.sort.reverse = !last.ascending
        }
        tableState.pagination.start = result.number * result.size // first element
        tableState.pagination.number = result.size // page size
        tableState.pagination.numberOfPages = result.totalPages
        tableState.pagination.totalItemCount = result.totalElements
        return result
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Void Partner Products
   *
   * @memberof models.Subscriber#
   */
  Subscriber.voidProduct = function (subscriberId, productId) {
    const url = `/nora/api/subscribers/${subscriberId}/partner-product-access/${productId}/void`
    return $http.patch(url)
  }

  /**
   * Activate Partner Products
   *
   * @memberof models.Subscriber#
   */
  Subscriber.activateProduct = function (subscriberId, productId) {
    const url = `/nora/api/subscribers/${subscriberId}/partner-product-access/${productId}/activate`
    return $http.patch(url)
  }

  /**
   * Get purchases
   *
   * @memberof models.Subscriber#
   */
  Subscriber.getPurchaseItems = function (tableState, subscriberId) {
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number || 25
    }
    Object.assign(params, tableState.search.predicateObject)

    if (params.processDate) {
      params['process-date-from'] = JSON.parse(params.processDate).after
      params['process-date-to'] = JSON.parse(params.processDate).before
      delete params.processDate
    }

    if (params.subscriptionStatus) {
      params['subscription-status'] = params.subscriptionStatus
      delete params.subscriptionStatus
    }

    if (params.purchaseStatus) {
      params['purchase-status'] = params.purchaseStatus
      delete params.purchaseStatus
    }

    if (params.purchaseNumber) {
      params['purchase-number'] = params.purchaseNumber
      delete params.purchaseNumber
    }

    if (params.productType) {
      params['product-type'] = params.productType
      delete params.productType
    }

    const url = `/nora/api/subscribers/${subscriberId}/purchases/items`

    return $http.get(url, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        var result = this.cast(data, {
          // response structure
          content: ['PaymentStatement'],
          first: true,
          last: true,
          number: 0,
          totalPages: 0,
          totalElements: 0,
          size: 0,
          sort: [{
            ascending: true,
            direction: '',
            ignoreCase: true,
            nullHandling: '',
            property: ''
          }]
        })

        if (Array.isArray(result.sort) && result.sort.length) {
          var last = result.sort.pop()
          tableState.sort.predicate = last.property
          tableState.sort.reverse = !last.ascending
        }
        tableState.pagination.start = result.number * result.size // first element
        tableState.pagination.number = result.size // page size
        tableState.pagination.numberOfPages = result.totalPages
        tableState.pagination.totalItemCount = result.totalElements
        return result
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  Subscriber.validateEmail = function (email) {
    return $http.post('/nora/api/validate/email', JSON.stringify(email)).then(response => {
      return response.data.payload
    })
  }
  /**
   * Update operator
   *
   * @memberof models.Subscriber#
   * @param {Number} subscriberId - use for only test!!!
   * @return {Object}
   */
  Subscriber.updateOperator = function (subscriberId) {
    return $http.post(`/nora/api/subscribers/${subscriberId}/verify-operator`).then(response => {
      return response.data.payload
    })
  }

  return Subscriber
}
